import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, Trophy, Calendar, DollarSign,
  CheckCircle, XCircle, AlertCircle, Loader2,
  TrendingUp, Activity, ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingScores, setPendingScores] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, scoresRes, tournamentsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/pending-scores`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tournaments`)
      ]);

      setStats(statsRes.data);
      setPendingScores(scoresRes.data);
      setTournaments(tournamentsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyScore = async (reportId, approved) => {
    setVerifying(reportId);
    try {
      await axios.put(`${API}/admin/verify-score/${reportId}`, 
        { approved, notes: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Score ${approved ? 'approved' : 'rejected'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to verify score');
    } finally {
      setVerifying(null);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">You need admin privileges to view this page.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">Monitor and manage your platform</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <Activity className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card data-testid="stat-users">
          <CardHeader className="pb-2">
            <CardDescription>Total Users</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-500" />
              {stats?.users?.total || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {stats?.users?.admins || 0} admins, {stats?.users?.organizers || 0} organizers
            </p>
          </CardContent>
        </Card>

        <Card data-testid="stat-tournaments">
          <CardHeader className="pb-2">
            <CardDescription>Tournaments</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <Trophy className="h-6 w-6 text-yellow-500" />
              {stats?.tournaments?.total || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {stats?.tournaments?.active || 0} active
            </p>
          </CardContent>
        </Card>

        <Card data-testid="stat-games">
          <CardHeader className="pb-2">
            <CardDescription>Games Played</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <Calendar className="h-6 w-6 text-green-500" />
              {stats?.games?.completed || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {stats?.games?.scheduled || 0} scheduled
            </p>
          </CardContent>
        </Card>

        <Card data-testid="stat-revenue">
          <CardHeader className="pb-2">
            <CardDescription>Total Revenue</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-emerald-500" />
              ${stats?.revenue?.total?.toFixed(2) || '0.00'}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Teams</CardDescription>
            <CardTitle className="text-2xl">
              {stats?.teams || 0}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className={stats?.scores_pending_verification > 0 ? 'border-yellow-500' : ''}>
          <CardHeader className="pb-2">
            <CardDescription>Pending Score Verifications</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              {stats?.scores_pending_verification || 0}
              {stats?.scores_pending_verification > 0 && (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* All Tournaments - system-wide access into every tournament's Director Panel */}
      <Card className="mb-8" data-testid="all-tournaments-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            All Tournaments
          </CardTitle>
          <CardDescription>Open any tournament's Director Panel to manage it directly</CardDescription>
        </CardHeader>
        <CardContent>
          {tournaments.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">No tournaments yet</p>
          ) : (
            <div className="space-y-2">
              {tournaments.map((t) => (
                <div
                  key={t.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border rounded-lg"
                  data-testid={`admin-tournament-row-${t.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{t.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.organizer_name || 'Unknown organizer'} · {t.start_date}
                      {t.end_date !== t.start_date && ` – ${t.end_date}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="capitalize">{t.status}</Badge>
                    <Link to={`/tournaments/${t.id}/director`}>
                      <Button size="sm" variant="outline" className="gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Director Panel
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Score Verifications */}
      <Card data-testid="pending-scores-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            Pending Score Verifications
          </CardTitle>
          <CardDescription>Review and verify game scores reported by officials</CardDescription>
        </CardHeader>
        <CardContent>
          {pendingScores.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p>All scores have been verified!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingScores.map((report) => (
                <div 
                  key={report.id} 
                  className="flex items-center justify-between p-4 border rounded-lg"
                  data-testid={`score-report-${report.id}`}
                >
                  <div>
                    <div className="font-medium">
                      {report.game?.team1_name || 'Team 1'} vs {report.game?.team2_name || 'Team 2'}
                    </div>
                    <div className="text-lg font-bold">
                      {report.team1_score} - {report.team2_score}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Reported: {new Date(report.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 border-green-600 hover:bg-green-50"
                      onClick={() => handleVerifyScore(report.id, true)}
                      disabled={verifying === report.id}
                      data-testid={`approve-score-${report.id}`}
                    >
                      {verifying === report.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-1" />
                      )}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => handleVerifyScore(report.id, false)}
                      disabled={verifying === report.id}
                      data-testid={`reject-score-${report.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
